import { getSession } from "@/auth/session";

const apiBaseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8787";

export class ApiError extends Error {
	readonly status: number;
	readonly code: string;

	constructor(status: number, message: string, code = "error") {
		super(message);
		this.status = status;
		this.code = code;
	}
}

type Listener = (error: ApiError) => void;
const unauthorizedListeners = new Set<Listener>();

/** AuthProvider subscribes so any 401 — sync, delta, plain fetch — logs the user out. */
export const onUnauthorized = (listener: Listener) => {
	unauthorizedListeners.add(listener);
	return () => {
		unauthorizedListeners.delete(listener);
	};
};

const membershipLostListeners = new Set<Listener>();

/** AuthProvider subscribes so a 403 `not_member` (removed from the workspace) refreshes the session. */
export const onMembershipLost = (listener: Listener) => {
	membershipLostListeners.add(listener);
	return () => {
		membershipLostListeners.delete(listener);
	};
};

const paymentRequiredListeners = new Set<Listener>();

/**
 * Billing subscribes so any 402 (`plan_limit`, `insufficient_credits`) — from `apiFetch` or a
 * rejected sync mutation — opens the upgrade / buy-credits dialog. Callers still get the error.
 */
export const onPaymentRequired = (listener: Listener) => {
	paymentRequiredListeners.add(listener);
	return () => {
		paymentRequiredListeners.delete(listener);
	};
};

export const notifyPaymentRequired = (error: ApiError) => {
	for (const listener of paymentRequiredListeners) listener(error);
};

export const PAYMENT_REQUIRED_CODES = new Set(["plan_limit", "insufficient_credits"]);

async function getAuthToken(): Promise<string | null> {
	for (let attempt = 0; attempt < 3; attempt++) {
		const token = getSession()?.token;
		if (token) return token;
		await new Promise((resolve) => setTimeout(resolve, 50));
	}
	return null;
}

export type ApiInit = RequestInit & { workspaceId?: string; auth?: boolean };

export async function apiFetch<T>(path: string, init: ApiInit = {}): Promise<T> {
	const { workspaceId, auth = true, ...rest } = init;
	const headers: Record<string, string> = { "Content-Type": "application/json" };
	const token = auth ? await getAuthToken() : null;
	if (token) headers.Authorization = `Bearer ${token}`;
	const ws = workspaceId ?? getSession()?.workspaceId;
	if (auth && ws) headers["X-Workspace-Id"] = ws;
	if (rest.headers) Object.assign(headers, rest.headers);

	const res = await fetch(`${apiBaseUrl}${path}`, { ...rest, headers });
	if (!res.ok) {
		const text = await res.text();
		let message = "Request failed";
		let code = "error";
		try {
			const json = JSON.parse(text) as { message?: string; code?: string };
			if (json.message) message = json.message;
			if (json.code) code = json.code;
		} catch {
			if (text) message = text;
		}
		const error = new ApiError(res.status, message, code);
		if (res.status === 401 && token) for (const listener of unauthorizedListeners) listener(error);
		if (res.status === 402) notifyPaymentRequired(error);
		if (res.status === 403 && code === "not_member")
			for (const listener of membershipLostListeners) listener(error);
		throw error;
	}
	return res.json() as Promise<T>;
}
